import React, { useState, useEffect } from "react";
import FaqTable from "../components/Inquiries/FaqTable";
import api from "../../utilis/api";
import { toast } from "react-toastify";

const ITEMS_PER_PAGE = 10;

const Faqs = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [currentFaq, setCurrentFaq] = useState(null);
  const [form, setForm] = useState({
    question: "",
    answer: "",
  });

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const fetchFaqs = async () => {
    try {
      setLoading(true);
      // For now, we'll use dummy data since the FAQs API might not exist yet
      // In a real implementation, you'd call: const res = await api.get("/faqs");
      const dummyFaqs = Array.from({ length: 8 }, (_, i) => ({
        _id: `faq_${i + 1}`,
        question: `What is ${["our return policy", "shipping time", "product quality", "payment methods", "size guide", "customer support", "order tracking", "product authenticity"][i]}?`,
        answer: `We provide detailed information about ${["returns and exchanges", "shipping and delivery", "quality assurance", "secure payments", "size measurements", "24/7 support", "real-time updates", "genuine products"][i]}. Our ${["policy", "service", "standards", "options", "guide", "team", "system", "guarantee"][i]} ensures customer satisfaction.`,
        createdAt: new Date(Date.now() - Math.random() * 365 * 24 * 60 * 60 * 1000).toISOString(),
      }));

      setFaqs(dummyFaqs);
    } catch (error) {
      console.error("Error fetching FAQs:", error);
      toast.error("Failed to load FAQs");
      setFaqs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFaqs();
  }, []);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleAdd = async () => {
    if (!form.question.trim() || !form.answer.trim()) {
      toast.error("Please fill in both question and answer");
      return;
    }

    try {
      // In a real implementation: await api.post("/faqs", form);
      const newFaq = {
        _id: `faq_${Date.now()}`,
        ...form,
        createdAt: new Date().toISOString(),
      };

      setFaqs([newFaq, ...faqs]);
      setIsAddOpen(false);
      setForm({ question: "", answer: "" });
      toast.success("FAQ added successfully");
    } catch (error) {
      toast.error("Failed to add FAQ");
    }
  };

  const handleEdit = (faq) => {
    setCurrentFaq(faq);
    setForm({ question: faq.question, answer: faq.answer });
    setIsEditOpen(true);
  };

  const handleUpdate = async () => {
    if (!form.question.trim() || !form.answer.trim()) {
      toast.error("Please fill in both question and answer");
      return;
    }

    try {
      // In a real implementation: await api.put(`/faqs/${currentFaq._id}`, form);
      const updatedFaqs = faqs.map(faq =>
        faq._id === currentFaq._id ? { ...faq, ...form } : faq
      );

      setFaqs(updatedFaqs);
      setIsEditOpen(false);
      setCurrentFaq(null);
      setForm({ question: "", answer: "" });
      toast.success("FAQ updated successfully");
    } catch (error) {
      toast.error("Failed to update FAQ");
    }
  };

  const handleDelete = async (faqId) => {
    if (!window.confirm("Are you sure you want to delete this FAQ?")) return;

    try {
      // In a real implementation: await api.delete(`/faqs/${faqId}`);
      setFaqs(faqs.filter(faq => faq._id !== faqId));
      toast.success("FAQ deleted successfully");
    } catch (error) {
      toast.error("Failed to delete FAQ");
    }
  };

  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const pagedData = faqs.slice(startIndex, endIndex);

  return (
    <div className="flex flex-col w-full gap-4">
      {/* Header */}
      <div className="bg-[#121218] p-4 border border-white/10 rounded-lg">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="font-bold text-xl text-white">Manage FAQs</h1>
            <p className="text-gray-400 text-sm mt-1">
              {loading ? "Loading FAQs..." : `Total ${faqs.length} FAQs`}
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setIsAddOpen(true)}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition duration-200"
            >
              Add FAQ
            </button>
            <button
              onClick={fetchFaqs}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition duration-200"
            >
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="bg-[#121218] border border-white/10 rounded-lg p-4">
        {loading ? (
          <div className="text-center py-8">
            <div className="text-gray-400">Loading FAQs...</div>
          </div>
        ) : (
          <FaqTable
            data={pagedData}
            currentPage={currentPage}
            totalPages={Math.ceil(faqs.length / ITEMS_PER_PAGE)}
            onPageChange={handlePageChange}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
        )}
      </div>

      {/* Add FAQ Modal */}
      {isAddOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-[#121218] border border-white/10 rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold text-white mb-4">Add New FAQ</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Question
                </label>
                <input
                  type="text"
                  name="question"
                  value={form.question}
                  onChange={handleChange}
                  className="w-full px-3 py-2 bg-[#1a1a1f] border border-white/20 rounded-md text-white placeholder-gray-400"
                  placeholder="Enter question"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Answer
                </label>
                <textarea
                  name="answer"
                  value={form.answer}
                  onChange={handleChange}
                  rows="4"
                  className="w-full px-3 py-2 bg-[#1a1a1f] border border-white/20 rounded-md text-white placeholder-gray-400"
                  placeholder="Enter answer"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => {
                  setIsAddOpen(false);
                  setForm({ question: "", answer: "" });
                }}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition duration-200"
              >
                Cancel
              </button>
              <button
                onClick={handleAdd}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition duration-200"
              >
                Add FAQ
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit FAQ Modal */}
      {isEditOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-[#121218] border border-white/10 rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold text-white mb-4">Edit FAQ</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Question
                </label>
                <input
                  type="text"
                  name="question"
                  value={form.question}
                  onChange={handleChange}
                  className="w-full px-3 py-2 bg-[#1a1a1f] border border-white/20 rounded-md text-white placeholder-gray-400"
                  placeholder="Enter question"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Answer
                </label>
                <textarea
                  name="answer"
                  value={form.answer}
                  onChange={handleChange}
                  rows="4"
                  className="w-full px-3 py-2 bg-[#1a1a1f] border border-white/20 rounded-md text-white placeholder-gray-400"
                  placeholder="Enter answer"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => {
                  setIsEditOpen(false);
                  setCurrentFaq(null);
                  setForm({ question: "", answer: "" });
                }}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition duration-200"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdate}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition duration-200"
              >
                Update FAQ
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Faqs;
